import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2, Sparkles, Rocket, Shield, Users, Beaker, ArrowUpRight, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";

const BENEFITS = [
  { icon: Shield, title: "Verified badge on your profile & leaderboard rows" },
  { icon: Users, title: "Copy up to 5 traders (default: 2)" },
  { icon: Beaker, title: "Unlimited backtest history + saved strategies" },
  { icon: Sparkles, title: "Priority AI grading with extended reasoning" },
  { icon: Rocket, title: "Broker priority routing (once IBKR OAuth lands)" },
];

function priceLabel(p) {
  const amt = (p.amount_cents / 100).toFixed(p.amount_cents % 100 === 0 ? 0 : 2);
  if (!p.recurring) return `$${amt} once`;
  return `$${amt} / ${p.interval}`;
}

function daysUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.round(diff / 86400000));
}

export default function Pro() {
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState("pro_monthly");
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const location = useLocation();
  const nav = useNavigate();

  const loadMe = () => api.get("/billing/me")
    .then(({ data }) => setMe(data))
    .catch(() => setMe({ verified_pro: false }));

  const loadPlans = () => api.get("/billing/plans")
    .then(({ data }) => {
      setPlans(data);
      if (!data.some((p) => p.lookup_key === selected) && data.length) setSelected(data[0].lookup_key);
    })
    .catch(() => setPlans([]));

  useEffect(() => { loadMe(); loadPlans(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Pro unlocked! Welcome to the verified club.");
          setPolling(false);
          loadMe();
          nav("/pro", { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout expired. Try again when ready.");
          setPolling(false);
          nav("/pro", { replace: true });
          return;
        }
      } catch { /* ignore */ }
      if (attempts >= 10) {
        toast.info("Still processing — refresh in a minute.");
        setPolling(false);
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [location.search]);

  const startCheckout = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key: selected,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout failed");
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.origin + "/pro",
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Portal unavailable");
      setPortalBusy(false);
    }
  };

  const isPro = me?.verified_pro;
  const isSubscription = Boolean(me?.subscription_id);
  const daysLeft = daysUntil(me?.verified_pro_expires_at);
  const canTrial = !me?.trial_used;
  // Flow B legacy user: has verified_pro + expires_at but NO subscription_id
  const isFlowBLegacy = isPro && !isSubscription && me?.verified_pro_expires_at;

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="pro-root">
      <div className="text-center">
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">TradePilot Pro</p>
        <h1 className="font-display text-5xl md:text-6xl font-medium tracking-tight mt-2">
          Get verified. Get followed.
        </h1>
        <p className="text-gray-500 mt-4 max-w-xl mx-auto text-lg leading-relaxed">
          Unlock the verified badge, boost your leaderboard credibility, and turn on the perks that make traders want
          to copy you.
        </p>
      </div>

      {isFlowBLegacy && (
        <Card className="border border-brand rounded-2xl p-6 bg-gray-50" data-testid="flowb-convert-banner">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center shrink-0">
              <ArrowUpRight size={16} />
            </div>
            <div className="flex-1">
              <p className="font-medium">Upgrade to a real subscription</p>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                You&apos;re on our legacy 30-day Pass. Switch to <span className="font-medium">Monthly ($9.99/mo)</span>
                {" "}or <span className="font-medium">Annual ($99/yr, save 17%)</span> for hands-free renewal —
                your current expiry ({me.verified_pro_expires_at ? new Date(me.verified_pro_expires_at).toLocaleDateString() : "—"}) is preserved and extended.
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Button
                  onClick={() => { setSelected("pro_monthly"); startCheckout(); }}
                  data-testid="flowb-convert-monthly"
                  className="rounded-full bg-brand text-white h-9 px-4 text-sm"
                  disabled={busy}
                >
                  Switch to Monthly
                </Button>
                <Button
                  onClick={() => { setSelected("pro_annual"); startCheckout(); }}
                  data-testid="flowb-convert-annual"
                  variant="outline"
                  className="rounded-full h-9 px-4 text-sm"
                  disabled={busy}
                >
                  Switch to Annual (save 17%)
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {isPro && (
        <Card className="border-2 border-gain rounded-2xl p-8 text-center bg-gain-soft" data-testid="pro-active-card">
          <CheckCircle2 className="mx-auto text-gain" size={48} />
          <p className="font-display text-3xl font-medium mt-4">You&apos;re Pro ✓</p>
          <p className="text-gray-600 mt-2 text-sm">
            {isSubscription ? (
              <>
                <span className="font-medium">
                  {plans.find((p) => p.lookup_key === me.subscription_lookup_key)?.label || "Subscription"}
                </span>
                {" · "}
                <span data-testid="sub-status">{me.subscription_status}</span>
                {me.verified_pro_expires_at && (
                  <> · renews {new Date(me.verified_pro_expires_at).toLocaleDateString()}</>
                )}
                {me.cancel_at_period_end && (
                  <> · <span className="text-loss font-medium">cancels at period end</span></>
                )}
              </>
            ) : me.verified_pro_expires_at ? (
              <>
                Pass active until{" "}
                {new Date(me.verified_pro_expires_at).toLocaleDateString(undefined, {
                  year: "numeric", month: "long", day: "numeric",
                })}
                {daysLeft !== null && ` · ${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`}
              </>
            ) : "Pro active"}
            .
          </p>
          {me.portal_available && (
            <div className="mt-4">
              <Button
                onClick={openPortal}
                disabled={portalBusy}
                data-testid="manage-subscription-btn"
                variant="outline"
                className="rounded-full h-10 px-5 text-sm"
              >
                {portalBusy ? "Opening…" : (
                  <>Manage subscription <ArrowUpRight size={14} className="ml-1" /></>
                )}
              </Button>
              <p className="text-xs text-gray-500 mt-2">
                Update card, cancel, download invoices — all handled by Stripe.
              </p>
            </div>
          )}
        </Card>
      )}

      {!isPro && (
        <Card className="border border-gray-200 rounded-2xl p-8" data-testid="pro-upgrade-card">
          <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500 mb-4">Choose a plan</p>

          <div className="grid gap-3 md:grid-cols-3">
            {plans.map((p) => {
              const active = selected === p.lookup_key;
              const isAnnual = p.lookup_key === "pro_annual";
              return (
                <button
                  key={p.lookup_key}
                  type="button"
                  onClick={() => setSelected(p.lookup_key)}
                  data-testid={`plan-${p.lookup_key}`}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    active
                      ? "border-brand bg-brand text-white shadow-md"
                      : "border-gray-200 hover:border-gray-400"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{p.label}</p>
                    {isAnnual && (
                      <Badge className={active ? "bg-gain text-white text-[10px]" : "bg-gain-soft text-gain text-[10px]"}>
                        SAVE 17%
                      </Badge>
                    )}
                  </div>
                  <p className="font-display text-2xl font-medium mt-2 tabular-nums">
                    {priceLabel(p)}
                  </p>
                  {p.recurring && p.supports_trial && canTrial && (
                    <p className={`text-xs mt-1 flex items-center gap-1 ${active ? "text-gray-300" : "text-gray-500"}`}>
                      <CalendarClock size={12} /> {p.trial_days}-day free trial
                    </p>
                  )}
                  {!p.recurring && (
                    <p className={`text-xs mt-1 ${active ? "text-gray-300" : "text-gray-500"}`}>
                      One-time — 30 days of Pro
                    </p>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-xs text-gray-500">
              Promo codes? Apply them on the Stripe checkout page — <span className="font-mono">TRADEPILOT10</span> style codes work automatically.
            </p>
            <Button
              onClick={startCheckout}
              disabled={busy || polling || !selected}
              data-testid="pro-upgrade-btn"
              className="rounded-full bg-brand text-white hover:bg-brand-deep h-12 px-8 font-medium"
            >
              {busy ? "Redirecting…" : polling ? "Processing…" : "Continue to checkout"}
            </Button>
          </div>

          <div className="mt-8 grid gap-3 border-t border-gray-100 pt-6">
            {BENEFITS.map(({ icon: Icon, title }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gain-soft flex items-center justify-center shrink-0">
                  <Icon size={14} className="text-gain" />
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{title}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <p className="text-center text-xs text-gray-400">
        Payments powered by Stripe · Test mode · Use card <span className="font-mono">4242 4242 4242 4242</span> to try.
      </p>
    </div>
  );
}
